import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useAuth } from '../services/authContext';

const emptyForm = { title: '', content: '', targetRole: 'all', publishedAt: '', sendEmail: true };

const Announcements = () => {
  const { user }                    = useAuth();
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [isModalOpen, setModal]     = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const canManageAnnouncements      = user?.role === 'admin' || user?.role === 'teacher';

  const load = async () => {
    try {
      setLoading(true); setError('');
      const data = await apiService.get('notifications/announcements');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load announcements.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true); setError(''); setSuccess('');
      const payload = {
        ...form,
        publishedAt: form.publishedAt || new Date().toISOString().slice(0, 19).replace('T', ' '),
      };
      const res = await apiService.post('notifications/announcements', payload);
      const recipientCount = res?.data?.recipientCount ?? res?.recipientCount ?? 0;
      setSuccess(`Announcement sent to ${recipientCount} users.`);
      setModal(false); setForm(emptyForm); await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send announcement.');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await apiService.raw.delete(`notifications/announcements/${id}`);
      await load();
    } catch { setError('Failed to delete.'); }
  };

  if (loading) return <LoadingSpinner message="Loading announcements..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Announcements</h1>
        {canManageAnnouncements && <button className="btn btn-primary" onClick={() => setModal(true)}>+ New Announcement</button>}
      </div>

      {error   && <div className="error-message">{error}</div>}
      {success && <div className="success-message" style={{ background: '#d4edda', color: '#155724', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' }}>{success}</div>}

      {items.length === 0 ? (
        <div className="no-data-message">No announcements yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {items.map(item => (
            <div key={item.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.4rem' }}>{item.title}</h3>
                  <p style={{ margin: '0 0 0.75rem', color: 'var(--text-light)', whiteSpace: 'pre-wrap' }}>{item.content}</p>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', display: 'flex', gap: '1rem' }}>
                    <span>By: {item.author_name}</span>
                    <span>Target: {item.target_role === 'all' ? 'Everyone' : item.target_role}</span>
                    <span>{new Date(item.published_at).toLocaleString()}</span>
                  </div>
                </div>
                {canManageAnnouncements && (
                  <button className="btn-icon" onClick={() => handleDelete(item.id)} style={{ color: '#c62828', flexShrink: 0 }}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManageAnnouncements && (
        <Modal isOpen={isModalOpen} onClose={() => setModal(false)} title="New Announcement">
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>Title</label>
              <input name="title" value={form.title} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Content</label>
              <textarea name="content" value={form.content} onChange={handleChange} rows={5} required style={{ resize: 'vertical' }} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Send To</label>
                <select name="targetRole" value={form.targetRole} onChange={handleChange}>
                  <option value="all">Everyone</option>
                  <option value="student">Students Only</option>
                  <option value="teacher">Lecturers Only</option>
                  <option value="parent">Parents Only</option>
                </select>
              </div>
              <div className="form-group">
                <label>Schedule (leave blank for now)</label>
                <input type="datetime-local" name="publishedAt" value={form.publishedAt} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" name="sendEmail" id="send-email" checked={form.sendEmail} onChange={handleChange} />
              <label htmlFor="send-email" style={{ marginBottom: 0 }}>Also send via email</label>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Sending...' : 'Send Announcement'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Announcements;
